import React, { useState } from 'react';
import classNames from 'classnames';
import ListItem from 'components/shared/ContentTagging/Tag';
import RemoteAutoComplete from 'components/shared/RemoteAutoComplete';
import { Icon } from 'ui';
import request from 'utils/request_ajax';

const ExpandableList = ({ children, collapsible = true, collapseAt = 3 }) => {
  const [expanded, setExpanded] = useState(false);

  const list = React.Children.toArray(children);

  const renderList = () => {
    if (expanded || list.length <= collapseAt || !collapsible) {
      return children;
    }

    return list.slice(0, collapseAt);
  };

  // prettier-ignore
  return list.length > 0 && (
    <div className={classNames('ExpandableList', { expanded })}>
      {renderList()}
      {collapsible && list.length > collapseAt && (
        <button
          type="button"
          className="u-linkBtn p-4"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? 'Show fewer'
            : `Show ${list.length - collapseAt} More`}
          <Icon
            name={`triangle_${expanded ? 'up' : 'down'}`}
            className="ml-4 fz-xs"
          />
        </button>
      )}
    </div>
  );
};

const LoadableList = ({
  list: { label, route, transformResponse, getItemLabel },
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);

  const toggleExpanded = () => {
    if (!expanded && !items) {
      setLoading(true);
      request
        .get(route)
        .then(data => {
          setItems(transformResponse(data));
          setLoading(false);
        })
        .catch(e => {
          setLoading(false);
          console.error(e);
        });
    }

    setExpanded(!expanded);
  };

  return (
    <div
      className={classNames('fz-base c-brand-gray-darker p-4 pos-r', {
        'mb-4': expanded,
      })}
    >
      <div className="ai-c d-f">
        <div
          className="c-p us-n"
          onClick={toggleExpanded}
          onKeyDown={e => e.key === 'Enter' && toggleExpanded}
        >
          <Icon name={expanded ? 'caret_down' : 'caret_right'} />
          <span>{label}</span>
        </div>
        {loading && <span className="Spinner ml-8 mt-1" />}
      </div>
      {expanded && items && items.length > 0 && (
        <div className="pl-16 mt-4 mb-0-lc">
          {items.map(item => (
            <ListItem key={item.id} name={getItemLabel(item)} />
          ))}
        </div>
      )}
    </div>
  );
};

// Return a (possible) subset of the suggestions array that has removed the intersection of selected items
const removeSelected = selected => suggestions => {
  return suggestions.filter(
    suggestion =>
      !selected.some(
        item => item.unique_identifier === suggestion.unique_identifier,
      ),
  );
};

const PeopleSelector = ({
  peopleLists = [],
  people = [],
  editing,
  toggleEditing,
  onSelect,
  route,
  mapResponseToSuggestions,
  filterSuggestions = removeSelected,
  renderPerson,
}) => {
  return (
    <div className="mb-0-lc">
      {editing && (
        <RemoteAutoComplete
          onSuggestionSelected={onSelect}
          placeholder={t('common.add_person_by_name')}
          className="NewInput NewInput--light NewInput--fw mb-16"
          url={route}
          filterSuggestions={filterSuggestions(people)}
          {...(mapResponseToSuggestions && { mapResponseToSuggestions })}
        />
      )}
      {peopleLists.map(list => (
        <LoadableList key={list.label} list={list} />
      ))}
      <ExpandableList collapsible={!editing}>
        {people.map(renderPerson)}
      </ExpandableList>
      {editing && (
        <div className="d-f jc-e ai-c mt-16">
          <button
            type="button"
            className="ml-16 NewButton NewButton--blue NewButton--wide NewButton--center"
            onClick={toggleEditing}
          >
            {t('common.done')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PeopleSelector;
